/**
 * DiscountSheet — right-side slide-over for create / edit discount
 *
 * Header: Tag icon with indigo→pink gradient (distinct from plan sheet's indigo→purple)
 * Shows DiscountStatusBadge in edit mode.
 */
import { Tag } from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@respectly/ui';
import { DiscountForm } from './DiscountForm';
import { DiscountStatusBadge } from './DiscountStatusBadge';
import type {
  DiscountWithStats,
  CreateDiscountDto,
  PlanWithStats,
} from '@respectly/shared-types';

// ─── Types ───────────────────────────────────────────────────────────────────

interface DiscountSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  discount?: DiscountWithStats;
  plans: PlanWithStats[];
  onSubmit: (dto: CreateDiscountDto) => Promise<boolean>;
  isLoading?: boolean;
  actionError?: { code: string; message: string } | null;
}

// ─── DiscountSheet ────────────────────────────────────────────────────────────

export function DiscountSheet({
  open,
  onOpenChange,
  discount,
  plans,
  onSubmit,
  isLoading,
  actionError,
}: DiscountSheetProps) {
  const isEditing = !!discount;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-[min(700px,100vw)] p-0 flex flex-col overflow-hidden border-l border-slate-200 dark:border-slate-700"
      >
        {/* ── Sticky Header ────────────────────────────────────────────── */}
        <SheetHeader className="shrink-0 px-6 pt-6 pb-5 border-b border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900">
          <div className="flex items-start justify-between gap-4">
            {/* Left: icon + title */}
            <div className="flex items-center gap-3 min-w-0">
              <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-indigo-500 to-pink-600 flex items-center justify-center shadow-sm shrink-0">
                <Tag className="h-5 w-5 text-white" />
              </div>
              <div className="min-w-0">
                <SheetTitle className="text-base font-semibold text-slate-900 dark:text-white leading-tight truncate">
                  {isEditing ? discount.name : 'New Discount'}
                </SheetTitle>
                <SheetDescription className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                  {isEditing
                    ? 'Edit discount details'
                    : 'Create a discount or promo code'}
                </SheetDescription>
              </div>
            </div>

            {/* Right: status badge (edit mode only) */}
            {isEditing && (
              <div className="shrink-0 pt-0.5">
                <DiscountStatusBadge discount={discount} />
              </div>
            )}
          </div>
        </SheetHeader>

        {/* ── Scrollable Body ──────────────────────────────────────────── */}
        <div className="flex-1 overflow-y-auto bg-slate-50 dark:bg-slate-950">
          {actionError && (
            <div className="mx-6 mt-5 rounded-lg bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-800 px-4 py-3">
              <p className="text-sm font-medium text-red-700 dark:text-red-400">
                {actionError.message}
              </p>
            </div>
          )}

          <div className="p-6">
            <DiscountForm
              discount={discount}
              plans={plans}
              onSubmit={onSubmit}
              onCancel={() => onOpenChange(false)}
              isLoading={isLoading}
            />
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
